import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { IFormProfile } from 'legacy/lut/classes/models/IFormProfile'

import { IContact } from 'clickNgo/entities/contact'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import { checkIsFailMessages } from 'dialer/helpers/messages'
import { IContactsWithMultiplePartners } from 'onboarding/features/models/contactsWithMultiplePartners'
import settings from 'src/config/settings'

import { IUpdateContactIdProps } from '../../models/onboardingContactModels'

class OnboardingContactApi {
  public async fetchByContactId(
    contactId: string,
    cacheKey?: string
  ): Promise<IFormProfile | null> {
    return CrudStore.get(CrudStoreItem.ContactActualData, cacheKey).getById({
      id: contactId,
      force: true,
      keyField: 'ContactID',
    })
  }

  public checkExistenceByEmail = async (partnerId: string, email: string) => {
    const wsClient = WebSocketStore.get('onboarding')
    const errorMessage = 'Failed to check contact existence'

    const response = await wsClient.call<{ exists: boolean }>({
      service: 'contact',
      action: 'check-existence-by-email',
      payload: { partnerId, email },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || errorMessage)
    }

    return response.data.exists
  }

  public generateNewContactId = async () => {
    const wsClient = WebSocketStore.get('onboarding')
    const errorMessage = 'Failed to generate new contact id'

    const response = await wsClient.call<{ contactId: string }>({
      service: 'contact',
      action: 'generate-new-contact-id',
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || errorMessage)
    }

    return response.data.contactId
  }

  public getExistingContactsByPartnerId = async (partnerId: string, accessToken: string) => {
    const endpointUrl = `${settings.httpsServices.onboarding}/api/v0/contact/getExistingContacts`
    const headers = {
      'Content-Type': 'application/json; charset=UTF-8',
      Authorization: `Bearer ${accessToken}`,
    }

    try {
      const response = await fetch(endpointUrl, {
        method: 'POST',
        body: JSON.stringify({ partnerId }),
        headers,
      })

      if (response.status === 200) {
        const data = await response.json()
        if (data.status === 'success') {
          return data.data as {
            contactId: string
            email: string | null
            partnerId: string
          }[]
        }
      }
      return []
    } catch (error) {
      Logger.error(error)
      return []
    }
  }

  public getContactsWithMultiplePartners = async (requestId: number) => {
    const wsClient = WebSocketStore.get('onboarding')

    const response = await wsClient.call<{ contacts: IContactsWithMultiplePartners[] }>({
      service: 'contact',
      action: 'get-contacts-with-multiple-partners',
      payload: { requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public updateContactId = async (data: IUpdateContactIdProps) => {
    const wsClient = WebSocketStore.get('onboarding')
    const errorMessage = 'Failed to update contactId'

    const response = await wsClient.call({
      service: 'contact',
      action: 'update-contact-id',
      payload: data,
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || errorMessage)
    }
  }

  public validateContact = async (contact: Partial<IContact>) => {
    const wsClient = WebSocketStore.get('onboarding')

    const result = await wsClient.call({
      service: 'contact',
      action: 'validate',
      payload: contact,
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    const isError = ['error', 'fail'].includes(result.status)
    if (isError) {
      let messages: string[] = []
      if (result.status === 'fail') {
        const failMessages = result.data?.error || ['Unknown error']

        if (checkIsFailMessages(failMessages)) {
          for (const message of failMessages) {
            if (message.message) {
              messages.push(message.message)
            }
          }
        } else {
          messages = failMessages
        }
      }

      if (result.status === 'error') {
        if (result.message) {
          messages.push(result.message)
        }
      }

      return {
        success: false,
        errorMessage: messages,
      }
    }
    return {
      success: true,
    }
  }
}

export const onboardingContactApi = new OnboardingContactApi()
